import {
  copyText,
  getConnectionTypeDrawerOptions,
  getCustomerUserDrawerOptions,
  getMarketAccountOptions,
  getTimeDrawerOptions,
  IconName,
  type ColumnDef,
  type CustomerUser,
  type EntityAdminClass,
  type ICustomerFIXConnection,
  type InputsAndDropdownsDrawerOption,
  type MarketAccount,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { useCallback } from 'react';
import { isActiveRuleColumn } from '../columns';

const BASE_CUSTOMER_FIX_CONNECTIONS_COLUMNS: ColumnDef<ICustomerFIXConnection>[] = [
  isActiveRuleColumn,
  {
    type: 'text',
    field: 'ConnectionID',
    initialWidth: 170,
    sortable: true,
    initialSort: '+',
    description: 'Unique ID of this connection',
    hide: true,
  },
  {
    type: 'text',
    field: 'Name',
    initialWidth: 180,
    sortable: true,
    initialSort: '-',
    description: 'Name of this connection',
  },
  {
    type: 'text',
    field: 'CustomerUser',
    initialWidth: 170,
    description: 'End user associated with this connection',
  },
  {
    type: 'text',
    field: 'ConnectionType',
    initialWidth: 200,
  },
  {
    type: 'text',
    field: 'URL',
    title: 'URL',
    initialWidth: 130,
  },
  {
    type: 'text',
    field: 'BeginString',
    initialWidth: 130,
    description: 'Usually FIX.4.4',
  },
  {
    type: 'text',
    field: 'SenderCompID',
    initialWidth: 120,
    description: 'Identifies the sending party for FIX traffic from this connection, the local side of the session.',
  },
  {
    type: 'text',
    field: 'TargetCompID',
    initialWidth: 150,
    description: 'Identifies the counterparty for FIX traffic on this session, the remote side of the connection.',
  },
  {
    type: 'time',
    field: 'StartTime',
    initialWidth: 150,
  },
  {
    type: 'time',
    field: 'EndTime',
    initialWidth: 150,
  },
];

const CUSTOMER_ACCOUNT_COLUMN: ColumnDef<ICustomerFIXConnection> = {
  type: 'text',
  field: 'MarketAccount',
  title: 'Customer Account',
  initialWidth: 160,
  description: 'Pinned customer account for this connection',
};

export function getCustomerFIXConnectionsColumns({
  showCustomerAccountSelector,
}: {
  showCustomerAccountSelector: boolean;
}): ColumnDef<ICustomerFIXConnection>[] {
  if (!showCustomerAccountSelector) {
    return BASE_CUSTOMER_FIX_CONNECTIONS_COLUMNS;
  }
  return [...BASE_CUSTOMER_FIX_CONNECTIONS_COLUMNS, CUSTOMER_ACCOUNT_COLUMN];
}

/** Shared FIX connection field options used by both EntityAdmin drawer and New FIX Connection workflow */
export function getFixConnectionFieldsDrawerOptions(): InputsAndDropdownsDrawerOption<ICustomerFIXConnection>[] {
  return [
    {
      field: 'ConnectionType',
      type: 'dropdown',
      title: 'Connection Type',
      getDropdownOptions: () => getConnectionTypeDrawerOptions(),
      getIsRequired: () => true,
    },
    { type: 'divider' },
    {
      field: 'URL',
      type: 'input',
      title: 'Port Number',
      getIsRequired: () => true,
      getInputPrefix: () => 'fix://:',
      transformControlToDrawerRecord: (value: string) => `fix://:${value}`,
      validate: form => (isNaN(Number(form.URL)) ? 'Port number must be an integer' : undefined),
    },
    {
      field: 'BeginString',
      type: 'input',
      title: 'Begin String',
      getIsRequired: () => true,
      transformDrawerRecordToControl: () => 'FIX.4.4',
      getIsDisabled: () => true,
    },
    {
      field: 'SenderCompID',
      type: 'input',
      title: 'SenderCompID',
      getIsRequired: () => true,
      guideText:
        'Identifies the sending party for FIX traffic from this connection, the local side of the session. Must differ from TargetCompID.',
    },
    {
      field: 'TargetCompID',
      type: 'input',
      title: 'TargetCompID',
      getIsRequired: () => true,
      validate: form =>
        form.TargetCompID && form.SenderCompID && form.TargetCompID === form.SenderCompID
          ? 'TargetCompID must differ from SenderCompID'
          : undefined,
      guideText:
        'Identifies the counterparty for FIX traffic on this session, the remote side of the connection. Must differ from SenderCompID.',
    },
    { type: 'divider' },
    {
      field: 'StartTime',
      type: 'dropdown',
      title: 'Start Time',
      getDropdownOptions: () => getTimeDrawerOptions(),
    },
    {
      field: 'EndTime',
      type: 'dropdown',
      title: 'End Time',
      getDropdownOptions: () => getTimeDrawerOptions(),
    },
  ];
}

export function getCustomerFixConnectionDrawerOptions({
  customerUsers,
  customerMarketAccounts,
  showCustomerAccountSelector,
}: {
  customerUsers: CustomerUser[];
  customerMarketAccounts: MarketAccount[];
  showCustomerAccountSelector: boolean;
}): InputsAndDropdownsDrawerOption<ICustomerFIXConnection>[] {
  return [
    {
      field: 'CustomerUser',
      type: 'dropdown',
      title: 'Customer User',
      getIsRequired: () => true,
      getDropdownOptions: () => getCustomerUserDrawerOptions(customerUsers),
      onChange: (_, onFormUpdate) => onFormUpdate('MarketAccount', undefined),
    },
    ...getFixConnectionFieldsDrawerOptions(),
    ...(showCustomerAccountSelector
      ? ([
          { type: 'divider' },
          {
            field: 'MarketAccount',
            type: 'dropdown',
            title: 'Customer Account',
            showClear: true,
            getIsDisabled: form => !form.CustomerUser,
            getDropdownOptions: form => {
              const customerUser = customerUsers.find(u => u.Email === form.CustomerUser);
              if (!customerUser) {
                return [];
              }
              return getMarketAccountOptions(
                customerMarketAccounts.filter(ma => ma.Counterparty === customerUser.Counterparty)
              );
            },
            guideText:
              "Pin this connection to a specific customer account. If omitted, the counterparty's default account is used.",
          },
        ] as InputsAndDropdownsDrawerOption<ICustomerFIXConnection>[])
      : []),
  ];
}

export const useGetCustomerFixConnectionContextMenuItems = () => {
  return useCallback((selectedEntities: GetContextMenuItemsParams<EntityAdminClass<ICustomerFIXConnection>>) => {
    return [
      {
        icon: `<i class="ag-icon ${IconName.ClipboardCopy}"/>`,
        name: `Copy Customer Details`,
        action: () => {
          if (!selectedEntities.node?.data) {
            return;
          }
          copyText(createCopiedCustomerDetailsTextForFixConnection(selectedEntities.node.data.data));
        },
      },
    ];
  }, []);
};

export const createCopiedCustomerDetailsTextForFixConnection = (
  fixConnection: Partial<
    Pick<ICustomerFIXConnection, 'BeginString' | 'TargetCompID' | 'SenderCompID' | 'URL' | 'StartTime' | 'EndTime'>
  >
): string => {
  const { BeginString, TargetCompID, SenderCompID, URL, StartTime, EndTime } = fixConnection;
  let text = '';

  const targetPort = URL?.split('fix://').at(1);

  if (TargetCompID) {
    text += `SenderCompID: ${TargetCompID}\n`;
  }
  if (SenderCompID) {
    // this is purposefully reversed
    text += `TargetCompID: ${SenderCompID}\n`;
  }
  if (targetPort) {
    text += `TargetPort: ${targetPort}\n`;
  }
  if (BeginString) {
    text += `BeginString: ${BeginString}\n`;
  }
  if (StartTime) {
    text += `StartTime: ${StartTime}\n`;
  }
  if (EndTime) {
    text += `EndTime: ${EndTime}\n`;
  }
  // remove trailing newline
  return text.trimEnd();
};
